// mock-entitlements-api.ts
import { EntitlementsApi } from "@entitlements/api-client"
import { ApiModels } from "@entitlements/api-models"

type Customer = ApiModels["Customer"]
type Subscription = ApiModels["Subscription"]
type ErrorResponse = ApiModels["ErrorResponse"]

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))
const nowSec = () => Math.floor(Date.now() / 1000)
const addDaysSec = (n: number) => nowSec() + n * 24 * 60 * 60

function withHttpError(code: number, errCode: string, message: string): never {
	const err: Error & { response?: ErrorResponse; status?: number } = new Error(message)
	err.status = code
	err.response = { error: errCode, message }
	throw err
}
function unauthorized(message = "You must be logged in"): never {
	return withHttpError(401, "UNAUTHORIZED", message)
}
function forbidden(message = "You are not authorized to access this resource"): never {
	return withHttpError(403, "FORBIDDEN", message)
}
function notFound(message: string): never {
	return withHttpError(404, "NOT_FOUND", message)
}

/** --- Seed Data (matches updated schema) -------------------------------- */

const subscriptionsDB: Record<string, Subscription> = {
	sub_01HZX3Y3ABCD1234: {
		id: "sub_01HZX3Y3ABCD1234",
		name: "LuxPlay Annual",
		type: "LUX_PLAY_ANNUAL",
		status: "ACTIVE",
		next_bill_date: addDaysSec(14), // seconds (UTC)
		edit_urls: [
			"https://billing.example.com/manage/sub_01HZX3Y3ABCD1234",
			"https://billing.example.com/manage/sub_01HZX3Y3ABCD1232",
		],
		next_refresh_date: addDaysSec(30), // optional, seconds (UTC)
		total_units: 1,
		available_units: 0,
	},
	// Unclaimed subscription for linking tests:
	sub_01UNCLAIMED123: {
		id: "sub_01UNCLAIMED123",
		name: "LuxAI Annual",
		type: "LUX_AI_ANNUAL",
		status: "ACTIVE",
		next_bill_date: addDaysSec(21),
		edit_urls: ["https://billing.example.com/manage/sub_01UNCLAIMED123"],
		next_refresh_date: addDaysSec(365),
		total_units: 1200,
		available_units: 1200,
	},
}

const customersDB: Record<string, Customer> = {
	"1": {
		id: "1",
		email_address: "user@example.com",
		subscriptions: ["sub_01HZX3Y3ABCD1234", "sub_01UNCLAIMED123"],
	},
}

/**
 * Optional auth simulation: set to a customer id to simulate a logged-in user.
 * If left undefined, read endpoints are open (handy for early dev).
 * The link endpoint requires auth (simulates real-world behavior).
 */
let currentUserId: string | undefined = undefined

const accessCheck = (customerId: string) => {
	if (currentUserId && currentUserId !== customerId) {
		forbidden()
	}
}
const accessCheckStrict = (customerId: string) => {
	if (!currentUserId) unauthorized()
	if (currentUserId !== customerId) forbidden()
}

/** --- Mock API ----------------------------------------------------------- */

class Api implements EntitlementsApi {
	/** Simulate variable network latency (150–450ms). */
	private async jitter() {
		await delay(150 + Math.floor(Math.random() * 300))
	}

	/** POST /customers/{id}/subscriptions (link) */
	async linkCustomerSubscription(customerId: string, subscriptionId: string): Promise<void> {
		await this.jitter()
		accessCheckStrict(customerId)

		const customer = customersDB[customerId]
		if (!customer) notFound(`Customer '${customerId}' not found`)

		const sub = subscriptionsDB[subscriptionId]
		if (!sub) notFound(`Subscription '${subscriptionId}' not found`)

		// Is the subscription already linked to *any* customer?
		for (const c of Object.values(customersDB)) {
			if (c.subscriptions.includes(subscriptionId)) {
				notFound(`Subscription '${subscriptionId}' is already linked to another customer`)
			}
		}

		if (!customer.subscriptions.includes(subscriptionId)) {
			customer.subscriptions.push(subscriptionId)
		}
		// Promise<void> per interface; real API would return 201 + message body.
	}

	async fetchCustomer(customerId: string): Promise<ApiModels["Customer"]> {
		await this.jitter()
		accessCheck(customerId)
		const customer = customersDB[customerId]
		if (!customer) notFound(`Customer '${customerId}' not found`)
		return { ...customer }
	}

	// NOTE: if your EntitlementsApi type still expects a map, change the return type back.
	async fetchCustomerSubscriptions(customerId: string): Promise<ApiModels["Subscription"][]> {
		await this.jitter()
		accessCheck(customerId)
		const customer = customersDB[customerId]
		if (!customer) notFound(`Customer '${customerId}' not found`)

		const list: ApiModels["Subscription"][] = []
		for (const subId of customer.subscriptions) {
			const sub = subscriptionsDB[subId]
			if (sub) list.push({ ...sub })
		}
		return list
	}

	async fetchCustomerSubscriptionById(
		customerId: string,
		subscriptionId: string,
	): Promise<ApiModels["Subscription"]> {
		await this.jitter()
		accessCheck(customerId)
		const customer = customersDB[customerId]
		if (!customer) notFound(`Customer '${customerId}' not found`)
		if (!customer.subscriptions.includes(subscriptionId)) {
			notFound(`Subscription '${subscriptionId}' not found for customer '${customerId}'`)
		}
		const sub = subscriptionsDB[subscriptionId]
		if (!sub) notFound(`Subscription '${subscriptionId}' not found`)
		return { ...sub }
	}

	/** ----- Extras to help testing (optional) ----- */
	setCurrentUser(id: string | undefined) {
		currentUserId = id
	}
	setSubscriptionStatus(subscriptionId: string, status: ApiModels["SubscriptionStatus"]) {
		const s = subscriptionsDB[subscriptionId]
		if (!s) notFound(`Subscription '${subscriptionId}' not found`)
		s.status = status
	}
	setSubscriptionUnits(
		subscriptionId: string,
		opts: Partial<Pick<Subscription, "total_units" | "available_units" | "next_refresh_date">>,
	) {
		const s = subscriptionsDB[subscriptionId]
		if (!s) notFound(`Subscription '${subscriptionId}' not found`)
		if (typeof opts.total_units === "number") s.total_units = opts.total_units
		if (typeof opts.available_units === "number") s.available_units = opts.available_units
		if (typeof opts.next_refresh_date === "number") s.next_refresh_date = opts.next_refresh_date
	}
}

export const mockEntitlementsApi = new Api()

// Simulate “logged in as 1” by default so link works out of the box
mockEntitlementsApi.setCurrentUser("1")

/* Example usage:

await mockEntitlementsApi.linkCustomerSubscription("1", "sub_01UNCLAIMED123");

const me   = await mockEntitlementsApi.fetchCustomer("1");
const subs = await mockEntitlementsApi.fetchCustomerSubscriptions("1");
const one  = await mockEntitlementsApi.fetchCustomerSubscriptionById("1", "sub_01HZX3Y3ABCD1234");

// Tweak units / dates for state testing:
mockEntitlementsApi.setSubscriptionUnits("sub_01HZX3Y3ABCD1234", {
  available_units: 40,
  next_refresh_date: Math.floor(Date.now()/1000) + 7*86400,
});
*/
